import React, { useState, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import authService from "../services/auth";
import PasswordInput from "../components/PasswordInput";
import {
  checkPasswordRequirements,
  isPasswordValid as checkIsPasswordValid,
  PASSWORD_REQUIREMENT_LABELS,
} from "../config/passwordValidation";
import { getSafeReturnPath, buildAuthLink } from "../utils/navigation";
import "./Login.css";

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Real-time password strength validation using centralized config
  const passwordChecks = useMemo(
    () => checkPasswordRequirements(formData.password),
    [formData.password],
  );

  const isPasswordValid = checkIsPasswordValid(formData.password);
  const passwordsMatch = formData.password === formData.confirmPassword;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate passwords match
    if (!passwordsMatch) {
      setError("Passwords do not match");
      return;
    }

    // Validate password requirements
    if (!isPasswordValid) {
      setError("Please meet all password requirements");
      return;
    }

    setLoading(true);

    try {
      await authService.register(formData.email, formData.password);
      navigate(getSafeReturnPath(searchParams.get("from")));
    } catch (err) {
      setError(err.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>Kwibl</h1>
        <h2>Create Account</h2>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="email"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <PasswordInput
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              autoComplete="new-password"
              disabled={loading}
              minLength={8}
            />
            {formData.password && (
              <div className="password-requirements">
                {Object.entries(PASSWORD_REQUIREMENT_LABELS).map(
                  ([key, label]) => (
                    <small
                      key={key}
                      className={passwordChecks[key] ? "valid" : "invalid"}
                    >
                      {passwordChecks[key] ? "\u2713" : "\u2717"} {label}
                    </small>
                  ),
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              autoComplete="new-password"
              disabled={loading}
              minLength={8}
            />
            {formData.confirmPassword && !passwordsMatch && (
              <small className="invalid">
                {"\u2717"} Passwords do not match
              </small>
            )}
            {formData.confirmPassword && passwordsMatch && (
              <small className="valid">{"\u2713"} Passwords match</small>
            )}
          </div>

          <button
            type="submit"
            className="btn-primary"
            disabled={loading || !isPasswordValid || !passwordsMatch}
          >
            {loading ? "Creating account..." : "Create Account"}
          </button>
        </form>

        <div className="login-footer">
          <p>
            Already have an account?{" "}
            <Link to={buildAuthLink("/login", searchParams.get("from"))}>
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
